import { useEffect, useRef } from 'react';

const generatePageviewId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for non-secure contexts / very old browsers. Collision risk is
  // session-scoped; uniqueness within a session is all we need for
  // $pageview_id ↔ replay linkage.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Route-alias redirects (e.g. /solution-session/X -> /office-hours/X) cause
// React Router to emit two location changes within ~50-60ms. Without
// debouncing, both fire $pageview events and the second carries a self-prev
// artifact because PostHog's internal pageview tracker is updated by the
// first capture and races our explicit prev. Suppress the first (the
// transient origin URL the user never actually sees rendered) by debouncing
// the fire — if the pathname changes again before the timer elapses, we
// reschedule with the new path.
const REDIRECT_DEBOUNCE_MS = 80;

/**
 * Fires `$pageview` on every `location.pathname` change. Synthesizes the
 * `$pageview_id` + `$prev_pageview_*` properties that PostHog auto-capture
 * normally attaches, so replay→event linkage and Paths-insight breadcrumbs
 * keep working. We do this manually because PostHog's pushState hook doesn't
 * reliably catch Ionic React Router navigations on this codebase —
 * auto-capture pageviews silently miss most SPA navs.
 *
 * `posthog.register({ $pageview_id })` writes the id as a super-property so
 * subsequent events (autocapture clicks, rageclicks, etc.) ride with the
 * same id between pageviews — matching PostHog's native behavior.
 *
 * Caller must set `capture_pageview: false` in PostHog init options to
 * avoid duplicate auto-pageviews.
 */
const AnalyticsPageView = ({ children, posthog, location }) => {
  const lastPathRef = useRef(null);
  const lastUrlRef = useRef(null);
  const viewStartRef = useRef(Date.now());
  const fireTimerRef = useRef(null);

  useEffect(() => {
    if (!posthog) return undefined;
    if (lastPathRef.current === location.pathname) return undefined;

    // Cancel any pending fire from a prior pathname change. If we're inside
    // a redirect chain, this drops the transient intermediate URL.
    if (fireTimerRef.current) clearTimeout(fireTimerRef.current);

    fireTimerRef.current = setTimeout(() => {
      fireTimerRef.current = null;

      const now = Date.now();
      const currentUrl = window.location.href;
      const pageviewId = generatePageviewId();

      posthog.register({ $pageview_id: pageviewId });

      const properties = {
        $current_url: currentUrl,
        $pathname: location.pathname,
        $pageview_id: pageviewId,
      };

      if (lastPathRef.current !== null) {
        properties.$prev_pageview_pathname = lastPathRef.current;
        properties.$prev_pageview_url = lastUrlRef.current;
        properties.$prev_pageview_duration = Math.round((now - viewStartRef.current) / 100) / 10;
      }

      posthog.capture('$pageview', properties);

      lastPathRef.current = location.pathname;
      lastUrlRef.current = currentUrl;
      viewStartRef.current = now;
    }, REDIRECT_DEBOUNCE_MS);

    return () => {
      if (fireTimerRef.current) {
        clearTimeout(fireTimerRef.current);
        fireTimerRef.current = null;
      }
    };
  }, [location, posthog]);

  return children;
};

export default AnalyticsPageView;
